import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
// Remove NoResultsMessage import if not used directly anymore
// import { NoResultsMessage } from '~/components/ui/EmptyStates/EmptyStates';
import { Loading } from '~/components/ui/Loading';
import { fadeVariants } from '~/utils/animations';

// Define the possible display states
export type DisplayState =
  | 'INITIAL_LOADING'
  | 'LOADING'
  | 'IDLE'
  | 'NO_RESULTS'
  | 'ERROR';

type StatefulViewProps = {
  displayState: DisplayState;
  children: React.ReactNode; // Content for IDLE state
  // Props needed to determine the correct "no results" message
  activeFilters?: boolean;
  isSearchResultEmpty?: boolean;
  entityName?: string; // Name of the entity being displayed (e.g., "items", "assets")
  className?: string;
};

// Default class names for containers (can be adjusted here)
const defaultLoadingClass =
  'flex h-64 pt-20 w-full items-center justify-center';
const defaultNoResultsClass =
  'flex h-full w-full flex-col items-center justify-start p-8 pt-20 text-center';
const defaultErrorClass =
  'text-error flex h-full w-full flex-col items-center justify-start p-8 pt-20 text-center';
const defaultIdleClass = 'flex-1 w-full';

// Internal component for the consistent "No Results" structure
const NoResultsContent = ({
  entityName,
  activeFilters,
  isSearchResultEmpty,
}: {
  entityName: string;
  activeFilters: boolean;
  isSearchResultEmpty: boolean;
}) => {
  let message = `No ${entityName} found. Please check back later or create new ${entityName}.`;
  if (isSearchResultEmpty) {
    message = `No ${entityName} found matching your search.`;
  } else if (activeFilters) {
    message = `There are no ${entityName} matching these filters. Try adjusting your filter criteria or clearing some filters.`;
  }

  return (
    <>
      <div className='mb-4 rounded-full bg-grey-5 p-6'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          className='text-grey-40'
        >
          <circle cx='11' cy='11' r='8'></circle>
          <path d='m21 21-4.3-4.3'></path>
        </svg>
      </div>
      <h3 className='mb-2 text-lg font-medium'>No results found</h3>
      <p className='max-w-md text-grey-40'>{message}</p>
    </>
  );
};

export function StatefulView({
  displayState,
  children,
  activeFilters = false,
  isSearchResultEmpty = false,
  entityName = 'items', // Default entity name
  className,
}: StatefulViewProps) {
  return (
    <AnimatePresence mode='wait'>
      {(displayState === 'INITIAL_LOADING' || displayState === 'LOADING') && (
        <motion.div
          key='loading'
          className={defaultLoadingClass}
          initial={fadeVariants.hidden}
          animate={fadeVariants.visible}
          exit={fadeVariants.exit}
        >
          <Loading spinner />
        </motion.div>
      )}

      {displayState === 'IDLE' && (
        <motion.div
          key='idle'
          className={classNames(defaultIdleClass, className)}
          initial={fadeVariants.hidden}
          animate={fadeVariants.visible}
          exit={fadeVariants.exit}
        >
          {children}
        </motion.div>
      )}

      {displayState === 'NO_RESULTS' && (
        <motion.div
          key='no-results'
          className={classNames(defaultNoResultsClass, className)}
          initial={fadeVariants.hidden}
          animate={fadeVariants.visible}
          exit={fadeVariants.exit}
        >
          {/* Use internal component for message logic */}
          <NoResultsContent
            entityName={entityName}
            activeFilters={activeFilters}
            isSearchResultEmpty={isSearchResultEmpty}
          />
        </motion.div>
      )}

      {displayState === 'ERROR' && (
        <motion.div
          key='error'
          className={classNames(defaultErrorClass, className)}
          initial={fadeVariants.hidden}
          animate={fadeVariants.visible}
          exit={fadeVariants.exit}
        >
          <p>Something went wrong. Please try again later.</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
